import React, { useState, useCallback, useRef } from 'react';
import { Photo } from '../../types';
import { PhotoUsage } from '../../types/photo';
import PhotoService from '../../services/photoService';
import { UploadTaskSnapshot } from 'firebase/storage';
import { useAuth } from '../../contexts/AuthContext';

interface PhotoUploadProps {
  personId: string;
  onUploadComplete: (uploadedPhotos: Photo[]) => void;
  onCancel?: () => void;
  maxFileSize?: number; // in MB, default 10MB
  acceptedFileTypes?: string[]; // default: common image types
  maxFiles?: number; // default 20
}

interface FilePreview {
  id: string;
  file: File;
  preview: string;
  caption: string;
  dateTaken: string;
  albumName: string;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  progress: number;
  error?: string;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  personId,
  onUploadComplete,
  onCancel,
  maxFileSize = 10,
  acceptedFileTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  maxFiles = 20
}) => {
  const { authState } = useAuth();
  const currentUser = authState.user;

  const [files, setFiles] = useState<FilePreview[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [overallProgress, setOverallProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Generate unique ID for file tracking
  const generateFileId = () => `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Validate file before adding to preview
  const validateFile = (file: File): string | null => {
    if (!acceptedFileTypes.includes(file.type)) {
      return `File type ${file.type} is not supported. Please use: ${acceptedFileTypes.join(', ')}`;
    }
    
    if (file.size > maxFileSize * 1024 * 1024) {
      return `File size ${(file.size / 1024 / 1024).toFixed(1)}MB exceeds maximum of ${maxFileSize}MB`;
    }
    
    return null;
  };

  // Create file preview object
  const createFilePreview = (file: File): FilePreview => {
    const preview = URL.createObjectURL(file);
    return {
      id: generateFileId(),
      file,
      preview,
      caption: '',
      dateTaken: new Date().toISOString().split('T')[0], // Today's date as default
      albumName: '',
      status: 'pending',
      progress: 0
    };
  };

  // Handle file selection (drag-drop or file input)
  const handleFiles = useCallback((newFiles: FileList | null) => {
    if (!newFiles) return;

    const fileArray = Array.from(newFiles);
    const validFiles: FilePreview[] = [];
    const errors: string[] = [];

    // Check total file count
    if (files.length + fileArray.length > maxFiles) {
      errors.push(`Cannot add ${fileArray.length} files. Maximum is ${maxFiles} files total.`);
      return;
    }

    fileArray.forEach(file => {
      const validationError = validateFile(file);
      if (validationError) {
        errors.push(`${file.name}: ${validationError}`);
      } else {
        validFiles.push(createFilePreview(file));
      }
    });

    if (errors.length > 0) {
      alert(`Upload errors:\n${errors.join('\n')}`);
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
    }
  }, [files.length, maxFiles, maxFileSize, acceptedFileTypes]);

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  // File input handler
  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    // Reset input to allow selecting same files again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [handleFiles]);

  // Remove file from preview
  const removeFile = useCallback((fileId: string) => {
    setFiles(prev => {
      const updated = prev.filter(f => f.id !== fileId);
      // Clean up object URL
      const fileToRemove = prev.find(f => f.id === fileId);
      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return updated;
    });
  }, []);

  // Update file metadata
  const updateFileMetadata = useCallback((fileId: string, field: keyof FilePreview, value: string) => {
    setFiles(prev => prev.map(f => 
      f.id === fileId ? { ...f, [field]: value } : f
    ));
  }, []);

  // Retry failed upload
  const retryFile = useCallback((fileId: string) => {
    setFiles(prev => prev.map(f => 
      f.id === fileId ? { ...f, status: 'pending', progress: 0, error: undefined } : f
    ));
  }, []);

  // Handle upload progress for individual files
  const handleProgress = useCallback((fileId: string, snapshot: UploadTaskSnapshot) => {
    const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
    
    setFiles(prev => prev.map(f => 
      f.id === fileId ? { 
        ...f, 
        progress,
        status: snapshot.state === 'running' ? 'uploading' : f.status
      } : f
    ));

    // Update overall progress
    setFiles(current => {
      const totalProgress = current.reduce((acc, file) => {
        if (file.id === fileId) {
          return acc + progress;
        }
        return acc + file.progress;
      }, 0);
      const avgProgress = Math.round(totalProgress / current.length);
      setOverallProgress(avgProgress);
      return current;
    });
  }, []);

  // Start upload process
  const handleUpload = useCallback(async () => {
    if (files.length === 0) return;

    setIsUploading(true);
    setOverallProgress(0);

    try {
      const uploadPromises = files
        .filter(f => f.status === 'pending' || f.status === 'error')
        .map(async (filePreview) => {
          try {
            // Update status to uploading
            setFiles(prev => prev.map(f => 
              f.id === filePreview.id ? { ...f, status: 'uploading' } : f
            ));

            // Upload single file with progress tracking
            const uploadedPhotos = await PhotoService.uploadPhotos(
              [filePreview.file],
              personId,
              currentUser?.uid || 'unknown',
              currentUser?.displayName || 'Unknown User',
              currentUser?.role || 'user',
              (snapshot) => handleProgress(filePreview.id, snapshot)
            );

            // Update metadata if provided
            if (uploadedPhotos.length > 0) {
              const photo = uploadedPhotos[0];
              
              // Update photo metadata if user provided any
              const hasMetadata = filePreview.caption || 
                                filePreview.dateTaken !== new Date().toISOString().split('T')[0];
              
              if (hasMetadata) {
                await PhotoService.updatePhotoMetadata(
                  photo.id,
                  {
                    caption: filePreview.caption,
                    dateTaken: filePreview.dateTaken,
                    albumId: filePreview.albumName || undefined
                  },
                  currentUser?.uid || 'unknown',
                  currentUser?.displayName || 'Unknown User',
                  currentUser?.role || 'user'
                );
              }

              // Mark as completed
              setFiles(prev => prev.map(f => 
                f.id === filePreview.id ? { ...f, status: 'completed', progress: 100 } : f
              ));

              return photo;
            }
            
            throw new Error('Upload completed but no photo returned');

          } catch (error) {
            console.error(`Upload failed for ${filePreview.file.name}:`, error);
            
            // Mark as error
            setFiles(prev => prev.map(f => 
              f.id === filePreview.id ? { 
                ...f, 
                status: 'error', 
                error: error instanceof Error ? error.message : 'Upload failed'
              } : f
            ));
            
            return null;
          }
        });

      // Wait for all uploads to complete
      const results = await Promise.all(uploadPromises);
      const successfulUploads = results.filter((photo: Photo | null): photo is Photo => photo !== null);

      if (successfulUploads.length > 0) {
        onUploadComplete(successfulUploads);
      }

      // Check if any uploads failed
      const failedCount = results.length - successfulUploads.length;
      if (failedCount > 0) {
        alert(`${failedCount} of ${results.length} uploads failed. You can retry failed uploads or cancel.`);
      } else {
        // All successful, can close
        setTimeout(() => {
          handleCancel();
        }, 1000);
      }

    } catch (error) {
      console.error('Batch upload error:', error);
      alert(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsUploading(false);
    }
  }, [files, personId, onUploadComplete, handleProgress]);

  // Cancel and cleanup
  const handleCancel = useCallback(() => {
    // Clean up object URLs
    files.forEach(file => {
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }
    });
    
    setFiles([]);
    onCancel?.();
  }, [files, onCancel]);

  const completedCount = files.filter(f => f.status === 'completed').length;
  const errorCount = files.filter(f => f.status === 'error').length;
  const pendingCount = files.filter(f => f.status === 'pending' || f.status === 'uploading').length;

  return (
    <div className="photo-upload">
      {/* Header */}
      <div className="upload-header">
        <h2>Upload Photos</h2>
        <button 
          onClick={handleCancel}
          className="cancel-btn"
          disabled={isUploading}
        >
          ✕
        </button>
      </div>

      {/* Drag and Drop Zone */}
      {files.length === 0 && (
        <div
          className={`drop-zone ${isDragOver ? 'drag-over' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="drop-zone-content">
            <div className="upload-icon">📷</div>
            <h3>Drag and drop photos here</h3>
            <p>or</p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="select-files-btn"
              disabled={isUploading}
            >
              Select Files
            </button>
            <p className="file-info">
              Supports: {acceptedFileTypes.map(type => type.split('/')[1]).join(', ')} 
              <br />
              Max size: {maxFileSize}MB each, {maxFiles} files total
            </p>
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptedFileTypes.join(',')}
        onChange={handleFileInput}
        style={{ display: 'none' }}
      />

      {/* File Previews */}
      {files.length > 0 && (
        <div className="file-previews">
          <div className="preview-header">
            <h3>Selected Photos ({files.length})</h3>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="add-more-btn"
              disabled={isUploading || files.length >= maxFiles}
            >
              Add More
            </button>
          </div>

          {/* Overall Progress */}
          {isUploading && (
            <div className="overall-progress">
              <div className="progress-info">
                <span>Overall Progress: {overallProgress}%</span>
                <span>
                  {completedCount} completed, {errorCount} failed, {pendingCount} remaining
                </span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${overallProgress}%` }}
                />
              </div>
            </div>
          )}

          <div className="preview-grid">
            {files.map((filePreview) => (
              <div key={filePreview.id} className="file-preview-card">
                {/* Preview Image */}
                <div className="preview-image">
                  <img src={filePreview.preview} alt={filePreview.file.name} />
                  
                  {/* Status Overlay */}
                  {filePreview.status === 'uploading' && (
                    <div className="status-overlay uploading">
                      <div className="spinner" />
                      <span>{filePreview.progress}%</span>
                    </div>
                  )}
                  
                  {filePreview.status === 'completed' && (
                    <div className="status-overlay completed">
                      <span>✓</span>
                    </div>
                  )}
                  
                  {filePreview.status === 'error' && (
                    <div className="status-overlay error">
                      <span>⚠</span>
                    </div>
                  )}
                  
                  {/* Remove Button */}
                  {filePreview.status !== 'uploading' && (
                    <button
                      onClick={() => removeFile(filePreview.id)}
                      className="remove-btn"
                      disabled={isUploading}
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* File Info */}
                <div className="file-info">
                  <div className="file-name" title={filePreview.file.name}>
                    {filePreview.file.name}
                  </div>
                  <div className="file-size">
                    {(filePreview.file.size / 1024 / 1024).toFixed(1)} MB
                  </div>
                </div>

                {/* Metadata Editing */}
                <div className="metadata-form">
                  <input
                    type="text"
                    placeholder="Caption"
                    value={filePreview.caption}
                    onChange={(e) => updateFileMetadata(filePreview.id, 'caption', e.target.value)}
                    disabled={isUploading}
                    className="caption-input"
                  />
                  
                  <input
                    type="date"
                    placeholder="Date taken"
                    value={filePreview.dateTaken}
                    onChange={(e) => updateFileMetadata(filePreview.id, 'dateTaken', e.target.value)}
                    disabled={isUploading}
                    className="date-input"
                  />
                  
                  <input
                    type="text"
                    placeholder="Album name (optional)"
                    value={filePreview.albumName}
                    onChange={(e) => updateFileMetadata(filePreview.id, 'albumName', e.target.value)}
                    disabled={isUploading}
                    className="album-input"
                  />
                </div>

                {/* Individual Progress */}
                {filePreview.status === 'uploading' && (
                  <div className="individual-progress">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${filePreview.progress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Error Display and Retry */}
                {filePreview.status === 'error' && (
                  <div className="error-info">
                    <div className="error-message">
                      {filePreview.error || 'Upload failed'}
                    </div>
                    <button
                      onClick={() => retryFile(filePreview.id)}
                      className="retry-btn"
                      disabled={isUploading}
                    >
                      Retry
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {files.length > 0 && (
        <div className="upload-actions">
          <button
            onClick={handleCancel}
            className="cancel-upload-btn"
            disabled={isUploading}
          >
            Cancel
          </button>
          
          <button
            onClick={handleUpload}
            className="start-upload-btn"
            disabled={isUploading || files.filter(f => f.status === 'pending' || f.status === 'error').length === 0}
          >
            {isUploading ? 'Uploading...' : `Upload ${files.filter(f => f.status === 'pending' || f.status === 'error').length} Photos`}
          </button>
        </div>
      )}

      {/* Upload Complete Summary */}
      {!isUploading && completedCount > 0 && (
        <div className="upload-summary">
          <h3>Upload Complete!</h3>
          <p>
            {completedCount} photo{completedCount !== 1 ? 's' : ''} uploaded successfully
            {errorCount > 0 && `, ${errorCount} failed`}
          </p>
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;